import { AxiosResponse, isAxiosError } from 'axios';
import { z } from 'zod';

import { API_DOMAIN, INAPP_BASE_URL } from 'config/constants';

import { api } from '../api';
import { checkStatus } from '../helpers';
import { dateFromString } from 'lib/zodHelpers';
import { zProduct } from './products';
import { zUser } from './syncReport';

export const zInAppAvailabilityException = z.object({
	date: dateFromString,
	limit: z.number(),
});

export enum OrderStatus {
	'active' = 'active',
	'cancelled' = 'cancelled',
	'closed' = 'closed',
}

export enum PaymentMethod {
	wallet = 'wallet',
	userBalance = 'userBalance',
	mercadopago = 'mercadopago',
}

export enum InAppSaleType {
	order = 'order',
	reservation = 'reservation',
}

export enum ReservationType {
	parking = 'parking',
	resort = 'resort',
}

export const orderStatus: OrderStatus[] = [OrderStatus.active, OrderStatus.cancelled, OrderStatus.closed] as const;
export const inAppSaleTypes: InAppSaleType[] = [InAppSaleType.order, InAppSaleType.reservation] as const;

export const reservationTypes: ReservationType[] = [ReservationType.parking, ReservationType.resort] as const;

export const zInAppAvailability = z.object({
	_id: z.string().optional(),
	product: z.string(),
	dailyLimit: z.number(),
	monthlyLimit: z.number(),
	exceptions: z
		.array(
			z.object({
				date: dateFromString,
				limit: z.number(),
			})
		)
		.optional(),
	type: z.nativeEnum(InAppSaleType),
	reservationType: z.nativeEnum(ReservationType).optional(),
	accessType: z.string().optional(),
});

export const zInAppAvailabilityArray = z.array(zInAppAvailability);

export const zInAppAvailabilityPopulated = zInAppAvailability.extend({
	product: zProduct,
});

export const zInAppAvailabilityPopulatedArray = z.array(zInAppAvailabilityPopulated);

export const zOrderUser = z.object({
	username: z.string(),
	email: z.string(),
	first_name: z.string(),
	last_name: z.string(),
});

export const zOrderProduct = z.object({
	productId: z.string(),
	title: z.string(),
	code: z.string().optional().nullable(),
	price: z.number(),
	quantity: z.number(),
	mixers: z
		.optional(
			z.array(
				z.object({
					mixer: z.string(),
					quantity: z.number(),
				})
			)
		)
		.nullable(),
});

export const zInAppSaleData = z.object({
	approvedBy: z.string().optional(),
	confirmationDate: dateFromString.optional(),
	refundedBy: z.string().optional(),
	refundDate: dateFromString.optional(),
	accessType: z.string().optional(),
	accessCode: z.string().optional(),
	plate: z.string().optional(),
	inAppSaleType: z.nativeEnum(InAppSaleType),
	isReservation: z.boolean(),
	reservationType: z.nativeEnum(ReservationType).optional(),
	reservationDate: dateFromString.optional(),
});

export const zOrder = z.object({
	_id: z.string(),
	code: z.string().optional(),
	// date: z
	// 	.string()
	// 	.or(dateFromString)
	// 	.transform((val) => {
	// 		if (typeof val === 'string') {
	// 			return new Date(val);
	// 		}
	// 		return val;
	// 	}),
	orderBy: zOrderUser,
	orderTo: zOrderUser,
	cancelledBy: zOrderUser.optional().nullable(),
	confirmedBy: zOrderUser.optional().nullable(),
	confirmationDate: dateFromString.optional().nullable(),
	cancellationDate: dateFromString.optional().nullable(),
	products: z.array(zOrderProduct),
	total: z.number(),
	tableNumber: z.optional(z.string().nullable()),
	status: z.nativeEnum(OrderStatus),
	section: z.string().optional().nullable(),
	approvals: z
		.array(
			z.object({
				date: dateFromString,
				status: z.string(),
				approvedBy: z.string(),
			})
		)
		.optional(),
	isInAppSale: z.boolean().optional(),
	inAppSaleData: zInAppSaleData.optional(),
	paymentMethod: z.nativeEnum(PaymentMethod),
	createdAt: dateFromString,
	updatedAt: dateFromString,
});

export enum InAppPopulatedModes {
	search = 'search',
	order = 'order',
	user = 'user',
}

const zInAppPopulatedSaleResult = z.object({
	total: z.number(),
	elements: z.array(zOrder),
	user: zUser.optional().nullable(),
	mode: z.nativeEnum(InAppPopulatedModes).optional(),
});

export type InAppPopulatedSaleResult = z.infer<typeof zInAppPopulatedSaleResult> & { error?: string };

export async function confirmSale(id: string): Promise<Order | null> {
	try {
		const response = await api.patch<Order>(`${API_DOMAIN}${INAPP_BASE_URL}sale/confirm/${id}`);
		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		const parsed = zOrder.safeParse(response.data);

		if (!parsed.success) {
			throw parsed.error;
		}
		return parsed.data;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function refundSale(id: string, adminKey: string): Promise<Order | null> {
	try {
		const response = await api.patch<Order>(`${API_DOMAIN}${INAPP_BASE_URL}sale/refund/${id}`, {
			adminKey,
			// We use the order id as the unique key for the request
			uniqueKey: id,
		});
		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		const parsed = zOrder.safeParse(response.data);

		if (!parsed.success) {
			throw parsed.error;
		}
		return parsed.data;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export type InAppSaleSearch = {
	createdAt?: Date | null;
	reservationDate?: Date | null;
	from?: string;
	to?: string;
	confirmationFrom?: string;
	confirmationTo?: string;
	cancellationFrom?: string;
	cancellationTo?: string;
	reservationFrom?: string;
	reservationTo?: string;
	user?: string;
	product?: string;
	vendor?: string;
	limit: number;
	offset: number;
	status?: string;
	statuses?: string;
	inAppSaleType?: string;
	reservationType?: string;
	section?: string;
	sections?: string;
	vendors?: string;
};

export async function listInAppSales(params: InAppSaleSearch): Promise<InAppPopulatedSaleResult> {
	let defaultResponse: InAppPopulatedSaleResult = {
		total: 0,
		elements: [],
	};

	try {
		const response = await api.get<InAppPopulatedSaleResult>(`${API_DOMAIN}${INAPP_BASE_URL}sale`, { params });
		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		const parsed = zInAppPopulatedSaleResult.safeParse(response.data);

		if (!parsed.success) {
			if ('user' in response.data) {
				defaultResponse.user = response.data.user as z.infer<typeof zUser>;
			}
			throw parsed.error;
		}
		return parsed.data;
	} catch (error) {
		console.error(error);
		return { ...defaultResponse, error: isAxiosError(error) ? error.response?.data : error };
	}
}

export async function listInAppAvailabilities(): Promise<InAppAvailability[]> {
	try {
		const response = await api.get<InAppAvailability[]>(`${API_DOMAIN}${INAPP_BASE_URL}availability`);
		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		const parsed = zInAppAvailabilityArray.safeParse(response.data);

		if (!parsed.success) {
			throw parsed.error;
		}
		return parsed.data;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export async function updateAvailability(availability: InAppAvailability): Promise<InAppAvailability> {
	try {
		let response: AxiosResponse<InAppAvailability> | undefined;

		if (availability._id) {
			response = await api.patch<InAppAvailability>(
				`${API_DOMAIN}${INAPP_BASE_URL}availability/${availability._id}`,
				availability
			);
		} else {
			response = await api.post<InAppAvailability>(`${API_DOMAIN}${INAPP_BASE_URL}availability`, availability);
		}

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		const parsed = zInAppAvailability.safeParse(response.data);

		if (!parsed.success) {
			throw parsed.error;
		}
		return parsed.data;
	} catch (error) {
		console.error(error);
		return availability;
	}
}

export async function deleteAvailability(availabilityId: string): Promise<boolean> {
	try {
		const response = await api.delete(`${API_DOMAIN}${INAPP_BASE_URL}availability/${availabilityId}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return true;
	} catch (error) {
		console.error(error);
		return false;
	}
}

export type InAppQRSale = {
	data: string;
	limit: number;
	offset: number;
	status?: string;
	statuses?: string;
	inAppSaleType?: string;
	reservationType?: string;
	username?: string | null;
	from?: string;
	to?: string;
	confirmationFrom?: string;
	confirmationTo?: string;
	cancellationFrom?: string;
	cancellationTo?: string;
	reservationFrom?: string;
	reservationTo?: string;
	sections?: string;
	vendors?: string;
};

export async function listInAppQRSales(data: InAppQRSale): Promise<InAppPopulatedSaleResult> {
	try {
		const response = await api.post<InAppPopulatedSaleResult>(`${API_DOMAIN}${INAPP_BASE_URL}sale/list/qr`, data);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		const parsed = zInAppPopulatedSaleResult.safeParse(response.data);

		if (!parsed.success) {
			throw parsed.error;
		}
		return parsed.data;
	} catch (error) {
		console.error(error);
		return { total: 0, elements: [] };
	}
}
